import { Injectable } from "@angular/core";
import { HttpBackend, HttpClient, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { Observable, of, throwError } from "rxjs";
import { catchError, finalize, map, shareReplay, switchMap } from "rxjs/operators";

import { AuthenticationService } from "@rubbl-core";

/**
 * A Rubbl API 401 is not, by itself, a dead identity session.
 *
 * POST /api/admin/v1/lead/session returns 401 when the marketplace token has no
 * phone_number claim, and 404 when that phone has no Connect lead. Both are a
 * valid OTP login. The JWT middleware only challenges with error="invalid_token"
 * when the token itself is rejected, and that header is not CORS-exposed, so the
 * browser cannot read it. Identity userinfo is the check that is visible here:
 * 401 means the bearer is bad; anything else means the session stays.
 */
@Injectable()
export class ApiUnauthorizedInterceptor implements HttpInterceptor {
  private tokenCheck: Observable<boolean> | null = null;
  private clearRequested = false;

  constructor(private authenticationService: AuthenticationService, private httpBackend: HttpBackend) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError(err => {
        const token = this.bearerToken(req);
        if (!token || !this.shouldConfirm(req, err)) {
          return throwError(() => err);
        }

        return this.tokenIsRejected(token).pipe(
          switchMap(rejected => {
            if (rejected && !this.clearRequested) {
              this.clearRequested = true;
              this.authenticationService.handleUnauthorizedApiResponse(true);
              Promise.resolve().then(() => {
                this.clearRequested = false;
              });
            }
            return throwError(() => err);
          }),
        );
      }),
    );
  }

  private shouldConfirm(req: HttpRequest<unknown>, err: unknown): boolean {
    if (!(err instanceof HttpErrorResponse) || err.status !== 401) {
      return false;
    }

    if (this.isLoginOrOtp(req.url) || this.isUserInfo(req.url)) {
      return false;
    }

    return this.isRubblApi(req.url);
  }

  private bearerToken(req: HttpRequest<unknown>): string {
    const header = req.headers.get("Authorization") || "";
    const match = /^Bearer\s+(\S+)/.exec(header);
    return match ? match[1] : "";
  }

  private isLoginOrOtp(url: string): boolean {
    return /RequestOtpApi|VerifyOtpApi|CompleteSpaLogin/i.test(url || "");
  }

  private isUserInfo(url: string): boolean {
    return /\/connect\/userinfo\/?$/i.test(url || "");
  }

  private isRubblApi(url: string): boolean {
    try {
      const host = new URL(url, window.location.origin).hostname.toLowerCase();
      return host.endsWith(".rubbl.com") && (host.includes("-api.") || host.startsWith("api."));
    } catch {
      return false;
    }
  }

  /** True only when Identity itself rejects this bearer. One check per burst of 401s. */
  private tokenIsRejected(token: string): Observable<boolean> {
    if (this.tokenCheck) {
      return this.tokenCheck;
    }

    const url = this.userInfoUrl();
    if (!url) {
      return of(false);
    }

    const client = new HttpClient(this.httpBackend);
    this.tokenCheck = client
      .get(url, {
        headers: { Authorization: `Bearer ${token}` },
        observe: "response",
      })
      .pipe(
        map(() => false),
        catchError(err => of(err instanceof HttpErrorResponse && err.status === 401)),
        finalize(() => {
          this.tokenCheck = null;
        }),
        shareReplay(1),
      );

    return this.tokenCheck;
  }

  private userInfoUrl(): string {
    const provider = ((window as any).environment && (window as any).environment.identityProvider) || "";
    if (!provider) {
      return "";
    }

    return `${String(provider).replace(/\/$/, "")}/connect/userinfo`;
  }
}
